<template>
	<Line 
    		ref="chart"
    		:data="computedData"
    		:options="chartOptions"
    		:plugins="plugins"
    	/>
</template>

<script>
import { Line } from 'vue-chartjs'
import { 
		Chart as ChartJS, 
		ArcElement, 
		LineController, 
		LineElement, 
		BarController, 
		BarElement, 
		PieController, 
		RadarController, 
		PointElement, 
		LinearScale, 
		RadialLinearScale, 
		CategoryScale, 
		TimeSeriesScale, 
		Title, 
		Legend, 
		Filler, 
		Tooltip,
		Utils, 
	} from 'chart.js'

import ChartDataLabels from 'chartjs-plugin-datalabels'
ChartJS.register(CategoryScale, ChartDataLabels, Filler, Legend, LineController, LineElement, LinearScale, TimeSeriesScale, Title, Tooltip);

import _ from 'lodash';
// import * as dayjs from 'dayjs'
import moment from 'moment'

export default {
	name: 'AreaChartStacked',
	components: {
		Line
	},
	props: {
		data: {
			type: Object,
			default: {}
		},
		options: {
			type: Object,
			default: {}
		},
		plugins: {
			type: Array,
			default: []
		},
		chartData: Object,
		tooltipDefaultConfig: Object,
		hasData: {
			type: Boolean,
			default: false
		},
		feTheme: {
			type: Object,
			default: {
				charts_no_data_text: 'No data'
			}
		}
			
	},
	data() {
		return {
            firstLoad: true, // When the page first loads
			cdOptions: this.chartData.options,
			formats: { 
                day: this.chartData.options.period.format['day'],
                week: this.chartData.options.period.format['week'],
                month: this.chartData.options.period.format['month'],
                year: this.chartData.options.period.format['year'],
            }
		}
	},
	mounted() {
		// console.log('Chart::', ChartJS)

	},
	computed: {
		minDate() {
			return new Date(this.chartData.options.minDate * 1000)
		},
		maxDate() {
            // if(this.chartData.title === 'Vorgänge') {
            //     console.log('Vorgänge this.chartData::', this.chartData.options.maxDate)
            // }
			return new Date(this.chartData.options.maxDate * 1000)
		},
		computedData() {
			if ( ! this.hasData ) {
				let data = JSON.parse(JSON.stringify(this.data));
				data.datasets = [
							{
								label: this.feTheme.charts_no_data_text,
								data: [1, 2, 3, 4, 2, 0],
								borderColor: '#c1d2c1',
			                    backgroundColor: '#d1d1d1',
			                    fill: {
			                    	'above': '#d1d1d1',
			                    	'below': '#d1d1d1',
			                    	'target': 'origin'
			                    }
							}
						]
				data.labels = ['', '', '', '', '', '']

                // console.log('computedData::', data)
				return data
			}
			return this.data
		},
		
		chartOptions() {
			const _this = this,
				cdOptions = this.cdOptions,
				xType = this.hasData ? 'time' : 'category';

                let xMin = this.hasData ? this.minDate : 0,
                    xMax = this.hasData ? this.maxDate : 6;

                /*
                 * If it's the first time when the page loads we set the last point on X-axis to be the current date,
                 * and also set the this.firstLoad to false so that if the user starts using the filter it will use the this.maxDate
                 *
                 * We also modify the start dates so that if:
                 * Chart period = daily => start date is set to -1 month
                 * Chart period = weekly => start date is set to -6 months
                 * Chart period = monthly => start date is set to -1 year
                 * Chart period = yearly => start date is set to -6 years
                 *
                 * Was discussed here: https://gitlab.com/ryter/custom/boxed/plugin/-/issues/275#note_1664201113
                 */
                if(this.firstLoad) {
                    this.firstLoad = false;

                    const period = this.chartData.period,
                        date = moment();

                    let startDate;

                    if(period && period === 'daily') {
                        startDate = date.subtract(1, 'months')
                    } else if(period && period === 'weekly') {
                        startDate = date.subtract(6, 'months')
                    } else if(period && period === 'monthly') {
                        startDate = date.subtract(1, 'years')
                    } else if(period && period === 'yearly') {
                        startDate = date.subtract(6, 'years')
                    } else {
                        startDate = date;
                    }

                    xMin = this.hasData ? startDate.valueOf() : 0
                    xMax = this.hasData ? Date.now() : 6
                }

			return {
				responsive: true,
				interaction: {
					intersect: false,
				},
				pointBackgroundColor: '#fff',
				plugins: {
					legend: {
						position: 'top',
					},

					filler: {
						propagate: true,
					},

					tooltip: {
						...this.tooltipDefaultConfig,

						callbacks: {
							title: function(context){
								// console.log(this)
								return _this.hasData ? _this.tooltipTitleCallback( context, this ) : this.feTheme.charts_no_data_text
							}, 
							// title: function(context) {
							// 	console.log(this)
							// 	console.log(context)
							// },
							labelColor: function(context) {
								// console.log()
								return {
									borderColor: context.dataset.borderColor,
									backgroundColor: context.dataset.backgroundColor,
									borderWidth: 0,
									// borderDash: [2, 2],
									borderRadius: 0,
									marginRight: 10,
								};
							},
							labelTextColor: function(context) {
								return '#fff';
							}
						}
					},
					datalabels: {
						display: false,
					},
				},
				maintainAspectRatio: false,
				scales: {
					x: {
						type : xType,
						min: xMin,
						max: xMax,
						time : {
							unit: cdOptions.period.unit,
							round: cdOptions.period.unit,
							// displayFormats: _.bind( _this.chartDisplayFormat, {cdOptions} )
							// displayFormats: this.displayFormats
						},
						ticks: {
							// callback: _.bind( _this.xTicksWeeksCallback, this )
							callback: function( value, s, t ) {
								if ( ! _this.hasData ) return '';
								let tickLabel = this.format(value, _this.formats[cdOptions.period.unit] )
								if ( 'week' == cdOptions.period.unit ) {
									return `W${tickLabel}`
								} else {
									return tickLabel
								}
							}
						}
					},
					y: {
						beginAtZero: true,
						suggestedMax: cdOptions.max ? cdOptions.max : 2,
						suggestedMin: cdOptions.min,
						stacked: true,
						grid: {
							tickLength: 10,
							drawOnChartArea: true
						},
						ticks: {
							stepSize: cdOptions.step_size_y ? cdOptions.step_size_y : .5,
							precision: 0,
						}
					}
				}
			}
		}
	},

	methods: {
		displayFormats() {
			return this.displayFormats
		},
		xTicksWeeksCallback(value, index, values) {
			if(this.chartData.options.period.unit === 'week') {
				// return `W${value}`;
			} else {
				return value;
			}
		},
		tooltipTitleCallback(context, a) {
			if ( this.chartData.options.period ) {
				let unit = this.chartData.options.period.unit		
				return `${'week' == unit ? 'W' : ''}${moment(new Date(context[0].raw.x))
					.format(window.date_format)}`
			}
		}
	}
}
</script>